import { supabase, toError } from '@/lib/supabase'
import type {
  Checklist,
  ChecklistInsert,
  ChecklistUpdate,
  ChecklistTemplate,
  ChecklistItem,
  ChecklistItemInsert,
  ChecklistItemUpdate,
} from '@/types/database'
import type {
  ChecklistEvidence,
  ChecklistEvidenceInsert,
  ChecklistEvidenceWithDocument,
} from '@/types/checklistEvidence'
import { profileService } from './profileService'

// Template item structure (embedded in template.items JSON)
interface TemplateItem {
  title: string
  description?: string | null
  requirement_ref?: string | null
}

export type ChecklistWithUsers = Checklist & {
  creator?: { full_name: string | null } | null
  updater?: { full_name: string | null } | null
}

const CL_SELECT = '*, creator:profiles!checklists_created_by_fkey(full_name), updater:profiles!checklists_updated_by_fkey(full_name)'

export const checklistService = {
  // ==========================================
  // CHECKLISTS
  // ==========================================

  /**
   * Get all checklists for the current organization
   */
  async getChecklists(): Promise<ChecklistWithUsers[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('checklists')
      .select(CL_SELECT)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      if (error.message?.includes('updated_by')) {
        const { data: fallback } = await supabase
          .from('checklists')
          .select('*, creator:profiles!checklists_created_by_fkey(full_name)')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
        return fallback ?? []
      }
      console.error('Error fetching checklists:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single checklist by ID with items
   */
  async getChecklist(id: string): Promise<ChecklistWithUsers | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('checklists')
      .select(CL_SELECT)
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching checklist:', error)
      return null
    }

    return data
  },

  /**
   * Get checklist items for a checklist with user profile data
   */
  async getChecklistItems(checklistId: string): Promise<ChecklistItem[]> {
    const { data, error } = await supabase
      .from('checklist_items')
      .select(`
        *,
        completed_by_profile:profiles!checklist_items_completed_by_fkey(
          id,
          full_name
        )
      `)
      .eq('checklist_id', checklistId)
      .order('sort_order')

    if (error) {
      // Fallback to simple query if join fails (FK may not exist)
      console.warn('Join query failed, falling back to simple query:', error)
      const { data: simpleData, error: simpleError } = await supabase
        .from('checklist_items')
        .select('*')
        .eq('checklist_id', checklistId)
        .order('sort_order')
      
      if (simpleError) {
        console.error('Error fetching checklist items:', simpleError)
        return []
      }
      return simpleData ?? []
    }

    return data ?? []
  },

  /**
   * Create a new checklist from a template
   */
  async createChecklist(
    checklist: Omit<ChecklistInsert, 'organization_id'>,
    templateId?: string
  ): Promise<Checklist | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    // Create the checklist
    const { data, error } = await supabase
      .from('checklists')
      .insert({
        ...checklist,
        organization_id: orgId,
        template_id: templateId,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating checklist:', error)
      throw toError(error)
    }

    // If a template was specified, create items from template
    if (templateId && data) {
      const template = await this.getTemplate(templateId)
      if (template?.items && Array.isArray(template.items)) {
        const items = template.items as unknown as TemplateItem[]
        await this.createItemsFromTemplate(data.id, items)
      }
    }

    return data
  },

  /**
   * Create checklist items from template items
   */
  async createItemsFromTemplate(checklistId: string, templateItems: TemplateItem[]): Promise<void> {
    const items: ChecklistItemInsert[] = templateItems.map((item, index) => ({
      checklist_id: checklistId,
      title: item.title,
      description: item.description,
      requirement_ref: item.requirement_ref,
      sort_order: index,
    }))

    const { error } = await supabase
      .from('checklist_items')
      .insert(items)

    if (error) {
      console.error('Error creating checklist items:', error)
      throw toError(error)
    }
  },

  /**
   * Update a checklist
   */
  async updateChecklist(id: string, updates: ChecklistUpdate): Promise<Checklist | null> {
    const { data: { user } } = await supabase.auth.getUser()
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data, error } = await supabase
      .from('checklists')
      .update({ ...updates, updated_by: user?.id })
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating checklist:', error)
      throw toError(error)
    }

    return data
  },

  /**
   * Update a checklist item
   */
  async updateChecklistItem(id: string, updates: ChecklistItemUpdate): Promise<ChecklistItem | null> {
    const { data: { user } } = await supabase.auth.getUser()

    const payload = { ...updates }

    // If marking as completed, add completion metadata
    if (payload.is_completed) {
      payload.completed_by = user?.id
      payload.completed_at = new Date().toISOString()
    } else if (payload.is_completed === false) {
      payload.completed_by = null
      payload.completed_at = null
    }

    const { data, error } = await supabase
      .from('checklist_items')
      .update(payload)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Error updating checklist item:', error)
      throw toError(error)
    }

    // Recalculate checklist progress
    if (data) {
      await this.recalculateProgress(data.checklist_id)
    }

    return data
  },

  /**
   * Recalculate checklist progress based on completed items
   */
  async recalculateProgress(checklistId: string): Promise<void> {
    const items = await this.getChecklistItems(checklistId)
    const applicableItems = items.filter(item => !item.is_not_applicable)
    const completedItems = applicableItems.filter(item => item.is_completed)

    const progress = applicableItems.length > 0
      ? Math.round((completedItems.length / applicableItems.length) * 100)
      : 0

    const { error } = await supabase
      .from('checklists')
      .update({
        progress,
        status: progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started'
      })
      .eq('id', checklistId)

    if (error) {
      console.error('Error updating checklist progress:', error)
      throw toError(error)
    }
  },

  /**
   * Delete a checklist
   */
  async deleteChecklist(id: string): Promise<boolean> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { error } = await supabase
      .from('checklists')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting checklist:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Get checklists by product
   */
  async getChecklistsByProduct(productId: string): Promise<ChecklistWithUsers[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('checklists')
      .select(CL_SELECT)
      .eq('product_id', productId)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching checklists by product:', error)
      return []
    }

    return data ?? []
  },

  // ==========================================
  // TEMPLATES
  // ==========================================

  /**
   * Get all available templates (system + organization)
   */
  async getTemplates(): Promise<ChecklistTemplate[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('checklist_templates')
      .select('*')
      .or(`is_system_template.eq.true,organization_id.eq.${orgId}`)
      .order('name')

    if (error) {
      console.error('Error fetching templates:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single template by ID
   */
  async getTemplate(id: string): Promise<ChecklistTemplate | null> {
    const { data, error } = await supabase
      .from('checklist_templates')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching template:', error)
      return null
    }

    return data
  },

  /**
   * Get checklist stats for dashboard
   */
  async getChecklistStats(): Promise<{
    total: number
    active: number
    completed: number
    overdue: number
  }> {
    const checklists = await this.getChecklists()
    const now = new Date()

    return {
      total: checklists.length,
      active: checklists.filter(c => c.status === 'in_progress').length,
      completed: checklists.filter(c => c.status === 'completed' || c.status === 'approved').length,
      overdue: checklists.filter(c =>
        c.due_date &&
        new Date(c.due_date) < now &&
        c.status !== 'completed' &&
        c.status !== 'approved'
      ).length,
    }
  },

  // ==========================================
  // CHECKLIST EVIDENCE
  // ==========================================

  /**
   * Get all evidence linked to a checklist item
   */
  async getItemEvidence(checklistItemId: string): Promise<ChecklistEvidenceWithDocument[]> {
    const { data, error } = await supabase
      .from('checklist_evidence')
      .select(`
        *,
        document:documents(*),
        linked_by_profile:profiles!checklist_evidence_linked_by_fkey(
          id,
          full_name
        )
      `)
      .eq('checklist_item_id', checklistItemId)
      .order('linked_at', { ascending: false })

    if (error) {
      // Fallback to simple query if join fails
      console.warn('Join query failed, falling back:', error)
      const { data: simpleData, error: simpleError } = await supabase
        .from('checklist_evidence')
        .select(`
          *,
          document:documents(*)
        `)
        .eq('checklist_item_id', checklistItemId)
        .order('linked_at', { ascending: false })

      if (simpleError) {
        console.error('Error fetching checklist item evidence:', simpleError)
        return []
      }
      return (simpleData ?? []) as ChecklistEvidenceWithDocument[]
    }

    return (data ?? []) as ChecklistEvidenceWithDocument[]
  },

  /**
   * Get all evidence for a checklist (across all items)
   */
  async getChecklistEvidence(checklistId: string): Promise<Map<string, ChecklistEvidenceWithDocument[]>> {
    const items = await this.getChecklistItems(checklistId)
    const itemIds = items.map(item => item.id)
    
    if (itemIds.length === 0) {
      return new Map()
    }

    const { data, error } = await supabase
      .from('checklist_evidence')
      .select(`
        *,
        document:documents(*),
        linked_by_profile:profiles!checklist_evidence_linked_by_fkey(
          id,
          full_name
        )
      `)
      .in('checklist_item_id', itemIds)
      .order('linked_at', { ascending: false })

    if (error) {
      // Fallback to simple query if join fails
      console.warn('Join query failed, falling back:', error)
      const { data: simpleData, error: simpleError } = await supabase
        .from('checklist_evidence')
        .select(`
          *,
          document:documents(*)
        `)
        .in('checklist_item_id', itemIds)
        .order('linked_at', { ascending: false })

      if (simpleError) {
        console.error('Error fetching checklist evidence:', simpleError)
        return new Map()
      }

      // Group by item ID
      const evidenceMap = new Map<string, ChecklistEvidenceWithDocument[]>()
      for (const item of items) {
        evidenceMap.set(item.id, [])
      }
      
      for (const evidence of (simpleData ?? []) as ChecklistEvidenceWithDocument[]) {
        const existing = evidenceMap.get(evidence.checklist_item_id) || []
        existing.push(evidence)
        evidenceMap.set(evidence.checklist_item_id, existing)
      }
      return evidenceMap
    }

    // Group by item ID
    const evidenceMap = new Map<string, ChecklistEvidenceWithDocument[]>()
    for (const item of items) {
      evidenceMap.set(item.id, [])
    }
    
    for (const evidence of (data ?? []) as ChecklistEvidenceWithDocument[]) {
      const existing = evidenceMap.get(evidence.checklist_item_id) || []
      existing.push(evidence)
      evidenceMap.set(evidence.checklist_item_id, existing)
    }

    return evidenceMap
  },

  /**
   * Link a document as evidence to a checklist item
   */
  async linkEvidence(evidence: ChecklistEvidenceInsert): Promise<ChecklistEvidence | null> {
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('checklist_evidence')
      .insert({
        ...evidence,
        linked_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error linking evidence:', error)
      throw toError(error)
    }

    return data as ChecklistEvidence
  },

  /**
   * Remove evidence link from a checklist item
   */
  async unlinkEvidence(evidenceId: string): Promise<boolean> {
    const { error } = await supabase
      .from('checklist_evidence')
      .delete()
      .eq('id', evidenceId)

    if (error) {
      console.error('Error unlinking evidence:', error)
      throw toError(error)
    }

    return true
  },

  /**
   * Update evidence notes
   */
  async updateEvidenceNotes(evidenceId: string, notes: string): Promise<ChecklistEvidence | null> {
    const { data, error } = await supabase
      .from('checklist_evidence')
      .update({ notes })
      .eq('id', evidenceId)
      .select()
      .single()

    if (error) {
      console.error('Error updating evidence notes:', error)
      throw toError(error)
    }

    return data as ChecklistEvidence
  },
}
